// SPDX-FileCopyrightText: 2025 TRUMPF Laser SE
//
// SPDX-License-Identifier: LicenseRef-TRUMPF
import React, { useCallback, useContext } from 'react';
import {
  AlgorithmProgrammingContext,
  prepareQCProgramForSave,
  QCPContext,
  setModifiedTimestamps,
} from '@tls/sw91_ui_templates';
import { useTranslation } from 'react-i18next';
import useQCPApi from 'hooks/useQCPApi';
import { Footer, FooterButton } from '@tls/treact-ui';
import { IconSave } from '@tls/treact-ui/icons';

interface Props {
  setIsEdit: (isEdit: boolean) => void;
}
export const AlgorithmProgrammingFooter = ({ setIsEdit }: Readonly<Props>) => {
  const { hasConfigurations, resetCompleteConfiguredProject, configuredProject, isQCPValid } =
    useContext(AlgorithmProgrammingContext);
  const { reloadProject, qcpProject } = useContext(QCPContext);
  const { postQCPProject } = useQCPApi();
  const { t } = useTranslation();
  const { plugins } = useContext(QCPContext);

  const qcpHasNoAlgorithmCalls =
    configuredProject?.instructions?.stages?.stage_03_calling_algorithms?.algorithm_calls?.length === 0;

  const apply = useCallback(async () => {
    if (configuredProject) {
      let preparedProgram = structuredClone(prepareQCProgramForSave(configuredProject, plugins));

      preparedProgram = setModifiedTimestamps(preparedProgram, qcpProject);

      preparedProgram.instructions.stages.stage_03_calling_algorithms.algorithm_calls =
        preparedProgram.instructions.stages.stage_03_calling_algorithms.algorithm_calls.map(algorithm => ({
          ...algorithm,
          enabled: true,
        }));

      try {
        await postQCPProject(preparedProgram);
        const qcprogram = await reloadProject();
        resetCompleteConfiguredProject(qcprogram);
        setIsEdit(false);
      } catch (error) {
        console.error(error);
      }
    }
  }, [
    configuredProject,
    reloadProject,
    resetCompleteConfiguredProject,
    postQCPProject,
    plugins,
    setIsEdit,
    qcpProject,
  ]);

  const cancel = useCallback(() => {
    resetCompleteConfiguredProject();
    setIsEdit(false);
  }, [resetCompleteConfiguredProject, setIsEdit]);

  return (
    <Footer
      right={[
        <FooterButton
          key={'abort'}
          variant='secondary'
          label={t('project.algorithms.footer.abort')}
          onClick={cancel}
          data-testid='algorithms-edit-cancel-button'
        />,
        <FooterButton
          key={'save'}
          disabled={!hasConfigurations || (!isQCPValid && !qcpHasNoAlgorithmCalls)}
          variant='primary'
          label={t('project.algorithms.footer.save')}
          icon={<IconSave />}
          onClick={apply}
          data-testid='algorithms-edit-save-button'
        />,
      ]}
    />
  );
};
